import { useCallback, useEffect, useMemo } from 'react';

import { useBreakpoint } from '@hivebrite/design-system/Theme';
import { useQueryString } from '@hivebrite/react-hooks';

import {
  MAP_VIEW_SETTINGS,
  VIEW_TYPE,
} from 'hb-react/shared/apps/Events/containers/Index/constants';
import { useFeature } from 'hb-react/shared/hooks/useFeature';

export const useEventViewType = () => {
  const {
    feature: { config },
  } = useFeature('events');

  const breakpoint = useBreakpoint();

  const [query, setQuery] = useQueryString();

  const {
    map_view_settings: mapViewSettings,
    event_calendar_view_enabled: eventCalendarViewEnabled,
  } = config;

  const mapCanBeDisplayed =
    breakpoint === 'xl' &&
    config.map_view_settings !== MAP_VIEW_SETTINGS.ALWAYS_HIDDEN;

  const defaultView =
    mapCanBeDisplayed && Boolean(config.map_visible === '1')
      ? VIEW_TYPE.MAP
      : VIEW_TYPE.LIST;

  const { view = defaultView } = query as { view?: keyof typeof VIEW_TYPE };

  const setView = useCallback((view) => setQuery({ view }), [setQuery]);

  const availableViews = useMemo(() => {
    const calendar = eventCalendarViewEnabled ? [VIEW_TYPE.CALENDAR] : [];

    if (
      breakpoint === 'l' ||
      mapViewSettings === MAP_VIEW_SETTINGS.ALWAYS_HIDDEN
    ) {
      return [VIEW_TYPE.LIST, ...calendar];
    }

    if (breakpoint === 'xl') {
      if (mapViewSettings === MAP_VIEW_SETTINGS.USER_DEFINED) {
        return [VIEW_TYPE.MAP, ...calendar, VIEW_TYPE.LIST];
      }

      if (mapViewSettings === MAP_VIEW_SETTINGS.ALWAYS_SHOWN) {
        return [VIEW_TYPE.MAP, ...calendar];
      }
    }

    return [VIEW_TYPE.LIST];
  }, [mapViewSettings, eventCalendarViewEnabled, breakpoint]);

  useEffect(() => {
    if (!availableViews.includes(view)) {
      setView(availableViews[0]);
    }
  }, [view, setView, availableViews]);

  return { view, setView, availableViews };
};
